import { useState, useEffect, useCallback } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetRevenueVsExpensesInsightsUseCase } from "../../domain/use-cases/GetRevenueVsExpensesInsightsUseCase";
import { GetCashInOutInsightsUseCase } from "../../domain/use-cases/GetCashInOutInsightsUseCase";

const repository = new ClientOverviewRepositoryImpl();
const getRevenueVsExpensesInsightsUseCase = new GetRevenueVsExpensesInsightsUseCase(repository);
const getCashInOutInsightsUseCase = new GetCashInOutInsightsUseCase(repository);

export interface TwelveMonthTrendInsightItem {
  title: string;
  body: string;
  sentiment: "positive" | "neutral" | "negative";
  rangeLabel?: string;
  monthCount?: number;
}

export interface TwelveMonthTrendInsightsData {
  revenueVsExpenses: TwelveMonthTrendInsightItem;
  cashInOut: {
    title: string;
    body: string;
    sentiment: "positive" | "neutral" | "negative";
    rangeLabel?: string;
    monthCount?: number;
    lastClosed: {
      monthLabel: string;
      cashIn: number | null;
      cashOut: number | null;
      net: number | null;
    };
  };
  loading: boolean;
  error: string | null;
}

export interface UseTwelveMonthTrendInsightsOptions {
  /** When false, the insights APIs are not called. Set to true only after twelve-month trend has loaded. */
  enabled?: boolean;
}

const defaultInsight: TwelveMonthTrendInsightItem = {
  title: "",
  body: "",
  sentiment: "neutral",
};

const defaultCashInsight: TwelveMonthTrendInsightsData["cashInOut"] = {
  title: "",
  body: "",
  sentiment: "neutral",
  lastClosed: { monthLabel: "—", cashIn: null, cashOut: null, net: null },
};

export function useTwelveMonthTrendInsights(
  options?: UseTwelveMonthTrendInsightsOptions,
): TwelveMonthTrendInsightsData {
  const enabled = options?.enabled ?? true;

  const [revenueVsExpenses, setRevenueVsExpenses] = useState<TwelveMonthTrendInsightItem>(defaultInsight);
  const [cashInOut, setCashInOut] =
    useState<TwelveMonthTrendInsightsData["cashInOut"]>(defaultCashInsight);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async () => {
    if (!enabled) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [revenueEntity, cashEntity] = await Promise.all([
        getRevenueVsExpensesInsightsUseCase.execute(),
        getCashInOutInsightsUseCase.execute(),
      ]);
      setRevenueVsExpenses({
        title: revenueEntity.title,
        body: revenueEntity.body,
        sentiment: revenueEntity.sentiment,
        rangeLabel: revenueEntity.rangeLabel,
        monthCount: revenueEntity.monthCount,
      });
      setCashInOut({
        title: cashEntity.title,
        body: cashEntity.body,
        sentiment: cashEntity.sentiment,
        rangeLabel: cashEntity.rangeLabel,
        monthCount: cashEntity.monthCount,
        lastClosed: cashEntity.lastClosed,
      });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load monthly trend insights",
      );
      setRevenueVsExpenses(defaultInsight);
      setCashInOut(defaultCashInsight);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchInsights();
  }, [enabled, fetchInsights]);

  return { revenueVsExpenses, cashInOut, loading, error };
}
